import type { WebhookEventFilters } from "~/lib/queries";

/**
 * Every filter on the webhook events list, as URL search params.
 *
 * Same contract as the transactions list: the URL is the only place these live,
 * so a filtered view is a link someone can paste into an incident thread.
 */
export interface WebhookEventSearch {
  source?: string;
  webhook_type?: string;
  event_type?: string;
  status?: string;
  order_ref_id?: string;
  external_id?: string;
  endpoint?: string;
  start_date?: string;
  end_date?: string;
  limit?: number;
  offset?: number;
}

/** Keys that filter the result set, as opposed to paging through it. */
const FILTER_KEYS = [
  "source",
  "webhook_type",
  "event_type",
  "status",
  "order_ref_id",
  "external_id",
  "endpoint",
  "start_date",
  "end_date",
] as const satisfies readonly (keyof WebhookEventSearch)[];

/**
 * The status the screen opens on when the URL names none.
 *
 * Failed first, and written into the address bar rather than applied invisibly.
 * A raw chronological firehose is much less useful than the handful of events
 * that went wrong, and those are why anyone opens this screen -- but a default
 * the URL does not mention is a default that lies to whoever you send the link
 * to. The All chip clears it.
 */
export const DEFAULT_STATUS = "failed";

/**
 * Reads search params off the URL.
 *
 * `status` is the one key that cannot simply be absent: absent means "the
 * operator has not chosen", which is where the failed-first default applies. An
 * explicit `status=` (what the All chip writes) means "no status filter", and
 * the two must stay distinguishable -- hence the sentinel below rather than
 * undefined for both.
 */
export function validateWebhookEventSearch(raw: Record<string, unknown>): WebhookEventSearch {
  return {
    source: str(raw.source),
    webhook_type: str(raw.webhook_type),
    event_type: str(raw.event_type),
    status: status(raw.status),
    order_ref_id: str(raw.order_ref_id),
    external_id: str(raw.external_id),
    endpoint: str(raw.endpoint),
    start_date: str(raw.start_date),
    end_date: str(raw.end_date),
    limit: num(raw.limit),
    offset: num(raw.offset),
  };
}

/** ALL is what the "All" chip puts in the URL: chosen, and chosen to be empty. */
export const ALL = "all";

function status(value: unknown): string | undefined {
  const parsed = str(value);
  return parsed === undefined ? DEFAULT_STATUS : parsed;
}

/** True when the result set is narrowed beyond the screen's own default. */
export function hasActiveFilters(search: WebhookEventSearch): boolean {
  return FILTER_KEYS.some((key) => {
    if (key === "status") return search.status !== DEFAULT_STATUS;
    return search[key] !== undefined;
  });
}

/** Maps the URL's shape onto the API's. ALL becomes no status filter at all. */
export function toFilters(search: WebhookEventSearch): WebhookEventFilters {
  return {
    source: search.source,
    webhook_type: search.webhook_type,
    event_type: search.event_type,
    status: search.status === ALL ? undefined : search.status,
    order_ref_id: search.order_ref_id,
    external_id: search.external_id,
    endpoint: search.endpoint,
    start_date: search.start_date,
    end_date: search.end_date,
    limit: search.limit ?? 25,
    offset: search.offset ?? 0,
  };
}

function str(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed === "" ? undefined : trimmed;
}

function num(value: unknown): number | undefined {
  const parsed = typeof value === "number" ? value : Number(str(value));
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
}
